import type { ReactNode } from 'react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { subjectColor, useSubjectLabel } from './subject-labels';
import { SubjectDot } from './subjects';
import type { Subject } from './usage-analytics.api';
import { ShareCell } from './usage-parts';

/**
 * Rows of something, a column per subject, a share in every cell.
 *
 * The shape most of this report's tables come to: what differs between them is
 * what a row is and what the share is of. Bars are drawn to the largest share
 * in the table, so a bar's length means the same thing in every column.
 */

export interface MatrixRow {
  id: string;
  label: ReactNode;
  /** By subject key. A subject with no entry gets a dash, not a zero. */
  cells: Map<string, { share: number; title?: string }>;
}

export function ShareMatrix({
  heading,
  subjects,
  rows,
  busy,
}: {
  /** What a row is, for the first column's header. */
  heading: string;
  subjects: Subject[];
  rows: MatrixRow[];
  busy?: boolean;
}) {
  const label = useSubjectLabel();
  const scale = Math.max(
    0,
    ...rows.flatMap((row) => [...row.cells.values()].map((cell) => cell.share))
  );

  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <div
        className={cn(
          'bg-card overflow-x-auto rounded-xl border',
          busy && 'opacity-60 transition-opacity'
        )}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{heading}</TableHead>
              {subjects.map((subject) => (
                <TableHead key={subject.key} className="min-w-36">
                  <span className="flex items-center gap-2">
                    <SubjectDot subject={subject} />
                    <span className="truncate">{label(subject)}</span>
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.label}</TableCell>
                {subjects.map((subject) => {
                  const cell = row.cells.get(subject.key);
                  return (
                    <TableCell key={subject.key} title={cell?.title}>
                      {cell ? (
                        <ShareCell
                          share={cell.share}
                          scale={scale}
                          color={subjectColor(subject)}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
